import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import moduleRealtimeService from '../../../services/moduleRealtimeService';

const ResourceAllocation = ({ data, onResourceUpdate }) => {
  const { projectId } = useParams();

  // Real-time subscription for resource changes
  React.useEffect(() => {
    if (!projectId) return;

    const resourceChannel = moduleRealtimeService?.subscribeToResourceChanges(
      projectId,
      (payload) => {
        console.log('Resource change detected:', payload?.event);
        
        // Trigger resource refresh when data changes
        if (onResourceUpdate) {
          onResourceUpdate();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      moduleRealtimeService?.unsubscribe(resourceChannel);
    };
  }, [projectId, onResourceUpdate]);

  if (!data) {
    return (
      <div className="bg-card rounded-xl border border-border p-3 min-w-0">
        <h2 className="text-sm font-semibold text-foreground mb-3 truncate">Resource Overview</h2>
        <p className="text-muted-foreground text-xs">No resource data available</p>
      </div>
    );
  }

  const resourceItems = [
    { 
      label: 'Supervision', 
      planned: data?.resources?.supervision?.planned || 85, 
      actual: data?.resources?.supervision?.actual || 78,
      icon: 'Users',
      color: '#8B5CF6'
    },
    { 
      label: 'Man Power', 
      planned: data?.resources?.manPower?.planned || 92, 
      actual: data?.resources?.manPower?.actual || 88,
      icon: 'UserCheck',
      color: '#3B82F6'
    },
    { 
      label: 'Equipment', 
      planned: data?.resources?.equipment?.planned || 75, 
      actual: data?.resources?.equipment?.actual || 82,
      icon: 'Wrench',
      color: '#F59E0B'
    },
    { 
      label: 'Materials', 
      planned: data?.resources?.materials?.planned || 88, 
      actual: data?.resources?.materials?.actual || 85,
      icon: 'Package',
      color: '#10B981'
    }
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-3 min-w-0">
      <h2 className="text-sm font-semibold text-foreground mb-3 truncate">Resource Overview</h2>
      <div className="space-y-4">
        {resourceItems?.map((item, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${item?.color}20` }}
                >
                  <Icon name={item?.icon} size={12} color={item?.color} />
                </div>
                <span className="text-xs font-medium text-foreground">{item?.label}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="text-xs text-muted-foreground">Planned: </span>
                  <span className="text-xs font-semibold text-foreground">{item?.planned}%</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-muted-foreground">Actual: </span>
                  <span 
                    className="text-xs font-semibold"
                    style={{ color: item?.actual >= item?.planned ? '#10B981' : '#EF4444' }}
                  >
                    {item?.actual}%
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Planned bar */}
              <div className="flex-1">
                <div className="bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${item?.planned}%`,
                      backgroundColor: `${item?.color}40`
                    }}
                  />
                </div>
              </div>
              {/* Actual bar */}
              <div className="flex-1">
                <div className="bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${item?.actual}%`,
                      backgroundColor: item?.color
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResourceAllocation;